import {Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {type PDFDocumentProxy} from 'pdfjs-dist'

import {fitPageThumb, READER_CONTROL_SIZE} from '#/lib/document/pdfFit'
import {PDF_SCROLL_AREA_CLASS} from '#/lib/document/pdfStyles.web'
import {useIsNearScreen} from '#/lib/document/useIsNearScreen'
import {atoms as a} from '#/alf'
import {useDocumentChrome} from '#/components/documents/documentChrome'
import {Text} from '#/components/Typography'
import {PdfPageCanvas} from '../PdfPageCanvas.web'

/**
 * How wide a tile in the rail is.
 *
 * There is deliberately no height beside it: a tile is a page drawn small, so its
 * height is the page's own shape at this width (`fitPageThumb`), not a second
 * measurement that could disagree with the document it is showing.
 */
export const THUMB_WIDTH = 84

export function ThumbnailRail({
  doc,
  docKey,
  ratio,
  totalPages,
  currentPage,
  onSelect,
}: {
  doc: PDFDocumentProxy
  docKey: string
  /** The document's page shape (width over height), so a tile can wear it. */
  ratio: number
  totalPages: number
  currentPage: number
  onSelect: (page: number) => void
}) {
  const chrome = useDocumentChrome()
  return (
    <div
      className={PDF_SCROLL_AREA_CLASS}
      style={{
        width: 116,
        flexShrink: 0,
        overflowY: 'auto',
        borderRight: `1px solid ${chrome.hairline}`,
        padding: '12px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}>
      {Array.from({length: totalPages}).map((_, index) => (
        <Thumbnail
          key={index}
          doc={doc}
          docKey={docKey}
          ratio={ratio}
          pageNumber={index + 1}
          isActive={index + 1 === currentPage}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

function Thumbnail({
  doc,
  docKey,
  ratio,
  pageNumber,
  isActive,
  onSelect,
}: {
  doc: PDFDocumentProxy
  docKey: string
  ratio: number
  pageNumber: number
  isActive: boolean
  onSelect: (page: number) => void
}) {
  const {_} = useLingui()
  const chrome = useDocumentChrome()
  const {isNear, ref} = useIsNearScreen(true, '900px 0px')
  /*
   * The page's own shape, not the rail's: a tile is as wide as the rail has room
   * for and as tall as that page's proportions ask for. A landscape worksheet gets
   * a wide tile and a portrait one a tall one — the same reading of the page the
   * stage below fits (`fitPagedPage`) and the carousel already draws by. The tile
   * carries a testID because the box is what a gate has to measure: the page inside
   * is drawn or not depending on the network, the box is there either way.
   */
  const thumb = fitPageThumb({width: THUMB_WIDTH, ratio})
  return (
    <div
      ref={ref}
      style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
      <Pressable
        testID={`documentThumb-${pageNumber}`}
        accessibilityRole="button"
        accessibilityLabel={_(msg`Go to page ${pageNumber}`)}
        accessibilityHint=""
        onPress={() => onSelect(pageNumber)}
        style={[
          {
            borderWidth: 2,
            borderRadius: 6,
            borderColor: isActive ? chrome.ink : chrome.hairline,
            overflow: 'hidden',
            // The thumbnail *is* a page, and a page is white in both modes.
            backgroundColor: '#fff',
          },
        ]}>
        {isNear ? (
          <PdfPageCanvas
            doc={doc}
            docKey={docKey}
            pageNumber={pageNumber}
            width={thumb.width}
            height={thumb.height}
            dpr={1}
          />
        ) : (
          <View
            style={{
              width: thumb.width,
              height: thumb.height,
              backgroundColor: chrome.placeholder,
            }}
          />
        )}
      </Pressable>
      <Text
        style={[
          a.text_2xs,
          a.mt_xs,
          {color: isActive ? chrome.ink : chrome.inkMuted},
        ]}>
        {pageNumber}
      </Text>
    </div>
  )
}

/**
 * A button in the reader's own furniture: the header, and the bar below the page.
 *
 * One ground only now. It used to be able to stand on the paper as well — the two
 * arrows that turned the page were drawn in the page's gutters or on the page
 * itself, depending on how the window fitted it — and the fill and the ink were
 * chosen for whichever ground it had landed on. The paging arrows are in the bar
 * with everything else, so a button of this kind only ever stands on chrome; the
 * controls that do sit in a page's corners (the clip, the audio, the author's `+`)
 * wear the film and the rim above instead.
 */
export function ChromeButton({
  label,
  onPress,
  disabled,
  children,
}: {
  label: string
  onPress: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  const chrome = useDocumentChrome()
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint=""
      disabled={disabled}
      onPress={onPress}
      style={({pressed}) => [
        {
          width: READER_CONTROL_SIZE,
          height: READER_CONTROL_SIZE,
          borderRadius: 99,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: chrome.surface,
          ...(chrome.surfaceEdge
            ? {borderWidth: 1, borderColor: chrome.surfaceEdge}
            : null),
          opacity: disabled ? 0.4 : pressed ? 0.75 : 1,
          cursor: disabled ? 'auto' : 'pointer',
        },
      ]}>
      {children}
    </Pressable>
  )
}
